import { defineStore } from 'pinia';
import { useAppDataStore } from '@/stores/app-data';
import { useUxData } from '@/stores/ux-data';
import { trackEvent } from '@/appdata/analytics';
import type { WorldInterface, SettingRuleInterface } from '@/types/worlds';
import type { SwadeCharacterInterface } from '@/types/characters';

export type GuidedFlowType = 'world' | 'character' | null;

export interface SourceEntry {
    world: WorldInterface;
    elementTypes: string[];
    // Set once this source's setting rules have been seeded into worldDraft. Persists across
    // WorldRulesStep remounts (step navigation) so manual removals are not re-added on re-visit.
    rulesSeeded?: boolean;
}

export interface GuidedSetupState {
    activeFlow: GuidedFlowType;
    currentStep: number;
    totalSteps: number;
    worldDraft: Partial<WorldInterface> | null;
    characterDraft: Partial<SwadeCharacterInterface> | null;
    selectedSources: SourceEntry[];
    stepValidation: Record<number, boolean>;
    isSubmitting: boolean;
    flowCompleted: boolean;
    flowReady: boolean;
}

export const useGuidedSetupStore = defineStore('guidedSetup', {
    state: (): GuidedSetupState => ({
        activeFlow: null,
        currentStep: 0,
        totalSteps: 0,
        worldDraft: null,
        characterDraft: null,
        selectedSources: [],
        stepValidation: {},
        isSubmitting: false,
        flowCompleted: false,
        flowReady: false,
    }),

    getters: {
        canProceed: (state): boolean => {
            return state.stepValidation[state.currentStep] ?? false;
        },
        // Next only needs the CURRENT step; Submit commits them all at once. A step that reports
        // `true` unconditionally (WorldRulesStep does) would otherwise leave Submit saying nothing
        // about whether the earlier steps still hold — the world flow's name step among them.
        // Only the step-gating kept a blank name off the API, which is too implicit to rely on.
        canSubmit: (state): boolean => {
            if (state.totalSteps <= 0) return false;

            for (let step = 0; step < state.totalSteps; step++) {
                if (state.stepValidation[step] !== true) return false;
            }

            return true;
        },
        canGoBack: (state): boolean => {
            return state.currentStep > 0;
        },
        isFirstStep: (state): boolean => {
            return state.currentStep === 0;
        },
        isLastStep: (state): boolean => {
            return state.currentStep === state.totalSteps - 1;
        },
        progressPercent: (state): number => {
            if (state.totalSteps === 0) return 0;
            return ((state.currentStep + 1) / state.totalSteps) * 100;
        },
        // The active flow's display name: the modal title, and the prefix of its analytics events.
        flowTitle: (state): string => {
            const labels = useUxData().labels?.guidedSetup;
            if (state.activeFlow === 'world') return labels?.worldSetup ?? 'World Setup';
            if (state.activeFlow === 'character') return labels?.characterSetup ?? 'Character Setup';
            return '';
        },
    },

    actions: {
        startWorldFlow(system: any) {
            this.activeFlow = 'world';
            this.currentStep = 0;
            this.totalSteps = 3;
            this.worldDraft = {
                name: '',
                summary: '',
                settingRules: [],
                system: system,
            };
            this.selectedSources = [];
            this.stepValidation = {};
            this.flowCompleted = false;
            this.isSubmitting = false;
            this.flowReady = false;
        },

        startCharacterFlow() {
            this.activeFlow = 'character';
            this.currentStep = 0;
            this.totalSteps = 7;
            this.characterDraft = null;
            this.selectedSources = [];
            this.stepValidation = {};
            this.flowCompleted = false;
            this.isSubmitting = false;
            this.flowReady = false;
        },

        nextStep() {
            if (this.currentStep < this.totalSteps - 1) {
                this.currentStep++;
            }
        },

        prevStep() {
            if (this.currentStep > 0) {
                this.currentStep--;
            }
        },

        goToStep(step: number) {
            if (step >= 0 && step < this.totalSteps) {
                this.currentStep = step;
            }
        },

        validateStep(step: number, isValid: boolean) {
            this.stepValidation[step] = isValid;
        },

        setSubmitting(value: boolean) {
            this.isSubmitting = value;
        },

        completeFlow() {
            this.flowCompleted = true;
            trackEvent(`${this.flowTitle}: Completed`);
        },

        setFlowReady(value: boolean) {
            this.flowReady = value;
        },

        resetFlow() {
            this.activeFlow = null;
            this.currentStep = 0;
            this.totalSteps = 0;
            this.worldDraft = null;
            this.characterDraft = null;
            this.selectedSources = [];
            this.stepValidation = {};
            this.flowCompleted = false;
            this.isSubmitting = false;
            this.flowReady = false;
        },

        addSettingRule(rule: SettingRuleInterface) {
            if (this.worldDraft && this.worldDraft.settingRules) {
                this.worldDraft.settingRules.push({ ...rule });
            }
        },

        removeSettingRule(rule: SettingRuleInterface) {
            if (this.worldDraft && this.worldDraft.settingRules) {
                this.worldDraft.settingRules = this.worldDraft.settingRules.filter(
                    (item) => item.uuid !== rule.uuid
                );
            }
        },

        // Seed the draft with the setting rules belonging to each selected source. The slim list
        // world in selectedSources carries no settingRules, so we load each source's full world via
        // retrieveWorld (recalculateCharacter=false, since we're building a world, not editing a
        // character) and read its settingRules. Rules are de-duplicated by uuid across sources.
        async seedSettingRulesFromSources() {
            if (!this.worldDraft) return;
            if (!Array.isArray(this.worldDraft.settingRules)) this.worldDraft.settingRules = [];

            const store = useAppDataStore();
            for (const entry of this.selectedSources) {
                if (entry.rulesSeeded) continue;
                const fullWorld = await store.retrieveWorld(entry.world, true, false);
                (fullWorld?.settingRules ?? []).forEach((rule) => {
                    const exists = this.worldDraft!.settingRules!.some((r) => r.uuid === rule.uuid);
                    if (!exists) this.worldDraft!.settingRules!.push({ ...rule });
                });
                entry.rulesSeeded = true;
            }
        },

        toggleSource(world: WorldInterface) {
            const idx = this.selectedSources.findIndex((s) => s.world.uuid === world.uuid);
            if (idx >= 0) {
                this.selectedSources.splice(idx, 1);
            } else {
                this.selectedSources.push({ world, elementTypes: [] });
            }
        },

        removeSource(world: WorldInterface) {
            this.selectedSources = this.selectedSources.filter(
                (s) => s.world.uuid !== world.uuid,
            );
        },

        toggleSourceType(world: WorldInterface, alias: string) {
            const entry = this.selectedSources.find((s) => s.world.uuid === world.uuid);
            if (!entry) return;
            if (alias === 'all') {
                entry.elementTypes = [];
                return;
            }
            if (entry.elementTypes.includes(alias)) {
                entry.elementTypes = entry.elementTypes.filter((t) => t !== alias);
            } else {
                entry.elementTypes.push(alias);
            }
        },

        updateWorldDraft(updates: Partial<WorldInterface>) {
            if (this.worldDraft) {
                Object.assign(this.worldDraft, updates);
            }
        },

        updateCharacterDraft(updates: Partial<SwadeCharacterInterface>) {
            if (this.characterDraft) {
                Object.assign(this.characterDraft, updates);
            }
        },

        setCharacterDraft(character: SwadeCharacterInterface) {
            this.characterDraft = character;
        },

        setTotalSteps(count: number) {
            this.totalSteps = count;
        },
    },
});
